import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api, imgUrl, money } from "../../lib/api";
import SafeImage from "../../components/common/SafeImage";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Trash2, MapPin, RotateCcw, Star, ShoppingBag } from "lucide-react";
import MeasurementsTab from "../../components/account/MeasurementsTab";
import AppointmentsTab from "../../components/account/AppointmentsTab";
import NotificationsTab from "../../components/account/NotificationsTab";
import { useFeatures } from "../../context/FeaturesContext";

const RETURN_REASONS = [
  "Size doesn't fit",
  "Quality not as expected",
  "Item damaged on arrival",
  "Different from description",
  "No longer needed",
  "Other",
];

function Empty({ icon: Icon, title, sub, cta, to }) {
  return (
    <div className="border border-dashed border-[hsl(var(--kb-border))] py-16 text-center">
      <Icon size={28} className="mx-auto text-[hsl(var(--kb-muted))] mb-3" />
      <div className="font-display text-xl mb-1">{title}</div>
      {sub && <div className="text-sm text-[hsl(var(--kb-muted))] mb-4">{sub}</div>}
      {cta && <Link to={to} className="kb-btn-outline inline-block">{cta}</Link>}
    </div>
  );
}

function OrdersTab({ orders, onReturn }) {
  if (orders.length === 0) {
    return <Empty icon={ShoppingBag} title="No orders yet" sub="Your purchase history will appear here." cta="Browse the bazaar" to="/shop" />;
  }
  const eligibleForReturn = (o) => o.payment_status === "paid" && !["cancelled", "refunded", "returned"].includes(o.status);
  return (
    <div className="space-y-6">
      {orders.map((o) => (
        <div key={o.id} className="border border-[hsl(var(--kb-border))] p-5" data-testid={`order-${o.id}`}>
          <div className="flex justify-between items-start mb-4 gap-3 flex-wrap">
            <div>
              <Link to={`/order/${o.id}`} className="overline text-[hsl(var(--kb-primary))] hover:underline" data-testid={`order-link-${o.id}`}>
                Order #{(o.order_number || o.id).slice(0, 14)}
              </Link>
              <div className="text-xs text-[hsl(var(--kb-muted))]">{new Date(o.created_at).toLocaleString()}</div>
            </div>
            <div className="text-right">
              <div className="text-[hsl(var(--kb-primary))] font-display text-xl">{money(o.total)}</div>
              <div className="text-xs uppercase tracking-widest">{(o.status || "").replace(/_/g, " ")}</div>
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {o.items.map((it, i) => (
              <Link to={`/product/${it.slug}`} key={i} className="flex gap-3 shrink-0 items-center">
                <SafeImage src={imgUrl(it.image)} className="w-16 aspect-[3/4] object-cover" alt="" />
                <div className="text-sm w-40">
                  <div className="line-clamp-2">{it.title}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">Qty {it.qty}{it.size ? ` · ${it.size}` : ""}</div>
                </div>
              </Link>
            ))}
          </div>
          {eligibleForReturn(o) && (
            <div className="pt-3 border-t border-[hsl(var(--kb-border))] mt-3">
              <button
                onClick={() => onReturn(o)}
                className="text-xs uppercase tracking-wider underline text-[hsl(var(--kb-primary))] flex items-center gap-1"
                data-testid={`request-return-${o.id}`}
              >
                <RotateCcw size={12} /> Request Return
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AddressesTab({ user, refresh }) {
  const [form, setForm] = useState({ label: "Home", name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });
  const [saving, setSaving] = useState(false);
  const addresses = user.addresses || [];

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.line1.trim() || !form.city.trim() || !form.state.trim() || !form.pincode.trim()) {
      return toast.error("Please fill name, phone, address, city, state and pincode");
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      return toast.error("Pincode must be 6 digits");
    }
    if (!/^\d{10,15}$/.test(form.phone.trim())) {
      return toast.error("Enter a valid phone number");
    }
    setSaving(true);
    try {
      await api.put("/auth/addresses", form);
      toast.success("Address added");
      setForm({ label: "Home", name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India" });
      refresh();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to add address");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (idx) => {
    try {
      await api.delete(`/auth/addresses/${idx}`);
      toast.success("Address removed");
      refresh();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div>
        <div className="overline mb-3">Saved Addresses</div>
        {addresses.length === 0 ? (
          <Empty icon={MapPin} title="No addresses saved" sub="Add an address to speed up checkout." />
        ) : (
          <div className="space-y-3">
            {addresses.map((a, i) => (
              <div key={i} className="border border-[hsl(var(--kb-border))] p-4 flex justify-between gap-2 text-sm" data-testid={`address-${i}`}>
                <div>
                  {a.label && <div className="overline mb-1">{a.label}</div>}
                  <div className="font-medium">{a.name} · {a.phone}</div>
                  <div>{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
                  <div className="text-[hsl(var(--kb-muted))]">{[a.city, a.state, a.pincode].filter(Boolean).join(", ")}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">{a.country}</div>
                </div>
                <button onClick={() => remove(i)} className="text-[hsl(var(--kb-muted))] hover:text-red-700" data-testid={`address-remove-${i}`} aria-label="Remove">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <form onSubmit={save} className="border border-[hsl(var(--kb-border))] p-5 space-y-3" data-testid="address-form">
        <div className="overline">Add Address</div>
        <div className="grid grid-cols-2 gap-3">
          <input className="border border-[hsl(var(--kb-border))] px-3 py-2 text-sm" placeholder="Full name"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="addr-name" />
          <input className="border border-[hsl(var(--kb-border))] px-3 py-2 text-sm" placeholder="Phone"
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="addr-phone" />
        </div>
        <input className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm" placeholder="Address line 1"
          value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} data-testid="addr-line1" />
        <input className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm" placeholder="Address line 2 (optional)"
          value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} data-testid="addr-line2" />
        <div className="grid grid-cols-2 gap-3">
          <input className="border border-[hsl(var(--kb-border))] px-3 py-2 text-sm" placeholder="City"
            value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="addr-city" />
          <input className="border border-[hsl(var(--kb-border))] px-3 py-2 text-sm" placeholder="State"
            value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="addr-state" />
          <input className="border border-[hsl(var(--kb-border))] px-3 py-2 text-sm" placeholder="Pincode"
            value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="addr-pincode" />
          <input className="border border-[hsl(var(--kb-border))] px-3 py-2 text-sm" placeholder="Country"
            value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="addr-country" />
        </div>
        <button className="kb-btn-primary w-full" disabled={saving} data-testid="addr-save-btn">
          {saving ? "Saving…" : "Save Address"}
        </button>
      </form>
    </div>
  );
}

function WishlistTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/wishlist").then(({ data }) => setItems(data.products || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (pid) => {
    try {
      await api.post(`/wishlist/toggle/${pid}`);
      setItems((cur) => cur.filter((p) => p.id !== pid));
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-[hsl(var(--kb-muted))]">Loading…</div>;
  if (items.length === 0) return <Empty icon={Heart} title="Wishlist is empty" sub="Save pieces you love here." cta="Browse the bazaar" to="/shop" />;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {items.map((p) => (
        <div key={p.id} className="group" data-testid={`wishlist-${p.slug}`}>
          <Link to={`/product/${p.slug}`}>
            <div className="aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] overflow-hidden mb-2">
              {p.images?.[0] && <SafeImage src={imgUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
            </div>
            <div className="text-sm line-clamp-2">{p.title}</div>
            <div className="text-sm font-medium text-[hsl(var(--kb-primary))]">{money(p.price)}</div>
          </Link>
          <button onClick={() => remove(p.id)} className="text-xs text-[hsl(var(--kb-muted))] underline mt-1" data-testid={`wl-remove-${p.slug}`}>
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}

function ReturnsTab({ returns, onRefresh }) {
  if (returns.length === 0) return <Empty icon={RotateCcw} title="No return requests" sub="Eligible orders can be returned within 7 days." />;
  const badge = (status) => {
    const cls = {
      pending: "bg-amber-100 text-amber-800",
      approved: "bg-emerald-100 text-emerald-800",
      rejected: "bg-red-100 text-red-800",
    }[status] || "bg-slate-100 text-slate-700";
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${cls}`}>{status}</span>;
  };
  return (
    <div className="space-y-3">
      {returns.map((r) => (
        <div key={r.id} className="border border-[hsl(var(--kb-border))] p-4 flex justify-between gap-3 flex-wrap" data-testid={`return-${r.id}`}>
          <div>
            <div className="overline mb-1">Return · Order {r.order_id.slice(0, 8)}</div>
            <div className="text-sm">{r.reason}</div>
            {r.note && <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">{r.note}</div>}
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">{new Date(r.created_at).toLocaleString()}</div>
            {r.admin_note && <div className="text-xs text-amber-700 mt-2">Admin: {r.admin_note}</div>}
          </div>
          <div className="flex flex-col items-end gap-2">
            {badge(r.status || "pending")}
            {r.refund_amount != null && <div className="text-sm font-medium">{money(r.refund_amount)} refund</div>}
          </div>
        </div>
      ))}
      <button onClick={onRefresh} className="text-xs underline text-[hsl(var(--kb-muted))]" data-testid="returns-refresh">Refresh</button>
    </div>
  );
}

function ReviewsTab() {
  const [revs, setRevs] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: orders } = await api.get("/orders/mine");
        const productIds = [...new Set(orders.flatMap((o) => o.items.map((i) => i.product_id)).filter(Boolean))].slice(0, 20);
        if (productIds.length === 0) { if (alive) setRevs([]); return; }
        const results = await Promise.allSettled(
          productIds.map((pid) => api.get(`/reviews/product/${pid}`).then(({ data }) => ({ pid, data })))
        );
        if (!alive) return;
        const all = [];
        results.forEach((r) => {
          if (r.status === "fulfilled" && r.value.data?.length) {
            const product = orders.flatMap((o) => o.items).find((i) => i.product_id === r.value.pid);
            all.push({ product_id: r.value.pid, product_title: product?.title, slug: product?.slug, reviews: r.value.data });
          }
        });
        setRevs(all);
      } catch {
        if (alive) setRevs([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (revs == null) return <div className="text-[hsl(var(--kb-muted))]">Loading…</div>;
  if (revs.length === 0) return <Empty icon={Star} title="No reviews yet" sub="Order something, and share what you think." cta="Browse the bazaar" to="/shop" />;

  return (
    <div className="space-y-4">
      <div className="text-xs text-[hsl(var(--kb-muted))]">Reviews on products you've ordered. Visit a product page to add yours.</div>
      {revs.map((g) => (
        <div key={g.product_id} className="border border-[hsl(var(--kb-border))] p-4">
          <Link to={`/product/${g.slug}`} className="overline text-[hsl(var(--kb-primary))]">{g.product_title}</Link>
          <div className="space-y-2 mt-2">
            {g.reviews.slice(0, 3).map((rv) => (
              <div key={rv.id} className="text-sm">
                <div className="flex items-center gap-2">
                  <div className="font-medium">{rv.user_name}</div>
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={11} className={i < rv.rating ? "fill-[hsl(var(--kb-accent))] text-[hsl(var(--kb-accent))]" : "text-[hsl(var(--kb-border))]"} />
                    ))}
                  </div>
                </div>
                <div className="text-xs text-[hsl(var(--kb-muted))]">{rv.comment}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Account() {
  const { user, refresh } = useAuth();
  const { has: featuresHas } = useFeatures();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnForm, setReturnForm] = useState({ reason: RETURN_REASONS[0], note: "" });
  const [submittingReturn, setSubmittingReturn] = useState(false);

  const loadOrders = () => api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => {});
  const loadReturns = () => api.get("/orders/mine/returns").then(({ data }) => setReturns(data)).catch(() => {});

  useEffect(() => {
    if (user) { loadOrders(); loadReturns(); }
  }, [user]);

  const submitReturn = async (e) => {
    e.preventDefault();
    if (!returnTarget || submittingReturn) return;
    setSubmittingReturn(true);
    try {
      await api.post("/orders/mine/returns", {
        order_id: returnTarget.id,
        reason: returnForm.reason,
        note: returnForm.note,
      });
      toast.success("Return request submitted");
      setReturnTarget(null);
      setReturnForm({ reason: RETURN_REASONS[0], note: "" });
      loadReturns();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed");
    } finally {
      setSubmittingReturn(false);
    }
  };

  if (!user) return <div className="px-6 py-24 text-center">Please sign in to see your account.</div>;

  const VALID_TABS = ["orders", "addresses", "wishlist", "returns", "measurements", "appointments", "notifications", "reviews", "profile"];
  const urlTab = searchParams.get("tab");
  const measurementsEnabled = featuresHas("measurements");
  const appointmentsEnabled = featuresHas("stitching_appointments");
  const desiredTab = VALID_TABS.includes(urlTab) ? urlTab : "orders";
  // If user lands on a feature-locked tab, fall back to Orders
  const currentTab =
    (desiredTab === "measurements" && !measurementsEnabled) ||
    (desiredTab === "appointments" && !appointmentsEnabled)
      ? "orders"
      : desiredTab;
  const onTabChange = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-10" data-testid="account-page">
      <div className="overline mb-2">Account</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-10">Hello, {user.name || user.phone.slice(-4)}</h1>

      <Tabs value={currentTab} onValueChange={onTabChange}>
        <TabsList className="rounded-none bg-transparent border-b border-[hsl(var(--kb-border))] p-0 h-auto flex-wrap justify-start">
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none px-4 md:px-6 py-3">Orders</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="tab-addresses" className="rounded-none px-4 md:px-6 py-3">Addresses</TabsTrigger>
          <TabsTrigger value="wishlist" data-testid="tab-wishlist" className="rounded-none px-4 md:px-6 py-3">Wishlist</TabsTrigger>
          <TabsTrigger value="returns" data-testid="tab-returns" className="rounded-none px-4 md:px-6 py-3">Returns</TabsTrigger>
          {measurementsEnabled && (
            <TabsTrigger value="measurements" data-testid="tab-measurements" className="rounded-none px-4 md:px-6 py-3">Measurements</TabsTrigger>
          )}
          {appointmentsEnabled && (
            <TabsTrigger value="appointments" data-testid="tab-appointments" className="rounded-none px-4 md:px-6 py-3">Appointments</TabsTrigger>
          )}
          <TabsTrigger value="notifications" data-testid="tab-notifications" className="rounded-none px-4 md:px-6 py-3">Notifications</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-none px-4 md:px-6 py-3">Reviews</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-none px-4 md:px-6 py-3">Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-8">
          <OrdersTab orders={orders} onReturn={(o) => setReturnTarget(o)} />
        </TabsContent>

        <TabsContent value="addresses" className="mt-8">
          <AddressesTab user={user} refresh={refresh} />
        </TabsContent>

        <TabsContent value="wishlist" className="mt-8">
          <WishlistTab />
        </TabsContent>

        <TabsContent value="returns" className="mt-8">
          <ReturnsTab returns={returns} onRefresh={loadReturns} />
        </TabsContent>

        {measurementsEnabled && (
          <TabsContent value="measurements" className="mt-8">
            <MeasurementsTab />
          </TabsContent>
        )}

        {appointmentsEnabled && (
          <TabsContent value="appointments" className="mt-8">
            <AppointmentsTab />
          </TabsContent>
        )}

        <TabsContent value="notifications" className="mt-8">
          <NotificationsTab />
        </TabsContent>

        <TabsContent value="reviews" className="mt-8">
          <ReviewsTab />
        </TabsContent>

        <TabsContent value="profile" className="mt-8">
          <div className="grid grid-cols-2 gap-6 max-w-lg">
            <div><div className="overline mb-2">Phone</div><div>{user.phone}</div></div>
            <div><div className="overline mb-2">Name</div><div>{user.name || "—"}</div></div>
            <div><div className="overline mb-2">Role</div><div className="capitalize">{user.role}</div></div>
          </div>
          <DeleteAccountSection user={user} />
        </TabsContent>
      </Tabs>

      {returnTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-4"
          onClick={() => setReturnTarget(null)}
          data-testid="return-modal"
        >
          <form
            onSubmit={submitReturn}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-lg p-6 space-y-4"
          >
            <div>
              <div className="overline">Request Return</div>
              <h2 className="font-display text-2xl">Order {(returnTarget.order_number || returnTarget.id).slice(0, 14)}</h2>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider mb-2">Reason</div>
              <select
                value={returnForm.reason}
                onChange={(e) => setReturnForm({ ...returnForm, reason: e.target.value })}
                className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm"
                data-testid="return-reason"
              >
                {RETURN_REASONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider mb-2">Additional notes (optional)</div>
              <textarea
                rows={3}
                value={returnForm.note}
                onChange={(e) => setReturnForm({ ...returnForm, note: e.target.value })}
                className="w-full border border-[hsl(var(--kb-border))] p-3 text-sm"
                data-testid="return-note"
              />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setReturnTarget(null)} className="kb-btn-outline flex-1" disabled={submittingReturn}>Cancel</button>
              <button className="kb-btn-primary flex-1" disabled={submittingReturn} data-testid="return-submit-btn">
                {submittingReturn ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/**
 * Delete Account block — soft-deletes the customer's account.
 * Required for Apple App Store guideline 5.1.1(v). Confirmation is
 * gated behind typing the literal word "DELETE" so a phone tap can't
 * accidentally wipe an account.
 */
function DeleteAccountSection({ user }) {
  const { logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const ok = confirm.trim().toUpperCase() === "DELETE";

  const submit = async () => {
    if (!ok || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/auth/delete-account");
      toast.success("Your account has been deleted.");
      // Clear local session immediately so the next paint shows the
      // signed-out storefront.
      await logout?.();
      // Hard reload to flush any in-memory state (cart, wishlist, etc.).
      setTimeout(() => { window.location.href = "/"; }, 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mt-12 pt-8 border-t border-[hsl(var(--kb-border))] max-w-2xl"
      data-testid="delete-account-section"
    >
      <h3 className="font-display text-2xl text-[hsl(var(--kb-primary))]">Delete your account</h3>
      <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 leading-relaxed">
        Deleting your account removes your name, phone, email, addresses,
        wishlist, cart, measurements, try-on history and push notifications
        from our servers immediately. Your past orders are kept for tax and
        compliance reasons but appear as <b>&ldquo;Deleted User&rdquo;</b> on the
        store&rsquo;s records.
      </p>
      <p className="text-xs text-[hsl(var(--kb-muted))] mt-2">
        This action is permanent and cannot be undone. You can sign up again
        later with the same phone number.
      </p>
      <button
        type="button"
        onClick={() => { setShowModal(true); setConfirm(""); }}
        className="mt-4 border border-[hsl(var(--kb-primary))] text-[hsl(var(--kb-primary))] px-6 py-3 uppercase text-sm tracking-wider hover:bg-[hsl(var(--kb-primary))] hover:text-white transition-colors"
        data-testid="delete-account-btn"
      >
        Delete my account
      </button>

      {showModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !submitting && setShowModal(false)}
          data-testid="delete-account-modal"
        >
          <div
            className="bg-white max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="font-display text-xl mb-2">Are you absolutely sure?</h4>
            <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
              This will erase your personal data from <b>{user?.phone}</b>.
              Type <b>DELETE</b> below to confirm.
            </p>
            <input
              type="text"
              className="kb-input mb-4"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type DELETE to confirm"
              autoFocus
              data-testid="delete-account-confirm-input"
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={submitting}
                className="kb-btn-outline flex-1"
                data-testid="delete-account-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!ok || submitting}
                className="kb-btn-primary flex-1 disabled:opacity-50"
                data-testid="delete-account-confirm-btn"
              >
                {submitting ? "Deleting…" : "Delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

